import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useAppContext } from './AppContext';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDate(ts: number) {
    const d = new Date(ts);
    return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
}

interface DateRangeSliderProps {
    onFilter: (months: string[]) => void;
    onClear: () => void;
}

export default function DateRangeSlider({ onFilter, onClear }: DateRangeSliderProps) {
    const { appData } = useAppContext();
    const receipts = appData.data.reportReceipts;

    // Stable extent — never shrinks when data filters
    const stableExtentRef = useRef<{ min: number; max: number } | null>(null);
    const dateExtent = useMemo(() => {
        const dates = receipts.map(r => r.transaction_date).filter(Boolean).sort();
        if (dates.length === 0) return null;
        return { min: new Date(dates[0]).getTime(), max: new Date(dates[dates.length - 1]).getTime() };
    }, [receipts]);

    if (dateExtent && !stableExtentRef.current) {
        stableExtentRef.current = dateExtent;
    }
    if (dateExtent && stableExtentRef.current) {
        if (dateExtent.min < stableExtentRef.current.min) stableExtentRef.current.min = dateExtent.min;
        if (dateExtent.max > stableExtentRef.current.max) stableExtentRef.current.max = dateExtent.max;
    }
    const extent = stableExtentRef.current;

    // Monthly buckets for mapping slider range → YM strings
    const monthBuckets = useMemo(() => {
        const map = new Map<string, boolean>();
        receipts.forEach(r => {
            if (!r.transaction_date) return;
            map.set(r.transaction_date.substring(0, 7), true);
        });
        return Array.from(map.keys()).sort();
    }, [receipts]);

    const [sliderRange, setSliderRange] = useState<[number, number] | null>(null);
    const [lo, setLo] = useState(extent?.min ?? 0);
    const [hi, setHi] = useState(extent?.max ?? 0);

    // Sync on reset
    React.useEffect(() => {
        if (!sliderRange && extent) { setLo(extent.min); setHi(extent.max); }
    }, [sliderRange, extent]);

    if (!extent || extent.min === extent.max) return null;

    const pctLo = ((lo - extent.min) / (extent.max - extent.min)) * 100;
    const pctHi = ((hi - extent.min) / (extent.max - extent.min)) * 100;

    const commit = (newLo: number, newHi: number) => {
        const lo2 = Math.min(newLo, newHi);
        const hi2 = Math.max(newLo, newHi);
        // Full range → clear
        if (lo2 <= extent.min && hi2 >= extent.max) {
            setSliderRange(null);
            onClear();
            return;
        }
        setSliderRange([lo2, hi2]);
        const startYm = `${new Date(lo2).getFullYear()}-${String(new Date(lo2).getMonth() + 1).padStart(2, '0')}`;
        const endYm = `${new Date(hi2).getFullYear()}-${String(new Date(hi2).getMonth() + 1).padStart(2, '0')}`;
        const selectedYMs = monthBuckets.filter(ym => ym >= startYm && ym <= endYm);
        if (selectedYMs.length > 0) onFilter(selectedYMs);
    };

    const clear = () => {
        setSliderRange(null);
        setLo(extent.min);
        setHi(extent.max);
        onClear();
    };

    return (
        <div style={{ padding: '8px 6px 2px 6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#000' }}>
                    {sliderRange ? `${formatDate(sliderRange[0])} → ${formatDate(sliderRange[1])}` : 'All time'}
                </span>
                {sliderRange && (
                    <span onClick={clear} style={{ fontSize: '0.65rem', cursor: 'pointer', color: 'rgba(0,0,0,0.5)', textDecoration: 'underline' }}>clear</span>
                )}
            </div>
            <div className="date-range-slider">
                <div className="date-range-slider__track">
                    <div className="date-range-slider__fill" style={{ left: `${Math.min(pctLo, pctHi)}%`, width: `${Math.abs(pctHi - pctLo)}%` }} />
                </div>
                <input type="range" min={extent.min} max={extent.max} value={lo}
                    className="date-range-slider__thumb"
                    onChange={e => setLo(Number(e.target.value))}
                    onMouseUp={() => commit(lo, hi)}
                    onTouchEnd={() => commit(lo, hi)}
                />
                <input type="range" min={extent.min} max={extent.max} value={hi}
                    className="date-range-slider__thumb"
                    onChange={e => setHi(Number(e.target.value))}
                    onMouseUp={() => commit(lo, hi)}
                    onTouchEnd={() => commit(lo, hi)}
                />
                <div className="date-range-slider__labels">
                    <span style={{ left: `${pctLo}%` }}>{sliderRange ? formatDate(lo) : ''}</span>
                    <span style={{ left: `${pctHi}%` }}>{sliderRange ? formatDate(hi) : ''}</span>
                </div>
            </div>
        </div>
    );
}
